<template>
<div :class="selectorClass">
    <div class="cfd-label"
        ref="label"
        @mousedown="labelMousedownHandler"
        @click="labelClickHandler">{{ label }} ({{ optionsCount }})</div>
    <div class="cfd-input"
        :tabindex="tabindex"
        ref="input"
        @focus="inputFocusHandler"
        @blur="inputBlurHandler"
        @click="inputClickHandler"
        @keydown.up.prevent="inputKeyUpHandler"
        @keydown.down.prevent="inputKeyDownHandler"
        @keydown.enter="inputEnterHandler"
        @keydown.esc="inputEscHandler"
        @keydown.tab="inputTabHandler">
        <div class="cfd-inner">
            <div class="cfd-selected">{{ selectedOptionsText }}</div>
            <div class="cfd-icon">
                <svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 10l5 5 5-5z"/>
                    <path d="M0 0h24v24H0z" fill="none"/>
                </svg>
            </div>
        </div>
        <transition name="fade" @before-enter="beforeEnter" @enter="enter" @after-leave="afterLeave" @leave-cancelled="leaveCancelled">
            <div class="cfd-dropdown" v-show="isOpened">
                <div :class="['cfd-option', { 'is-interminate': isInterminate, 'is-selected': areAllSelected, 'is-highlighted': isSelectAllOptionHighlighted }]" v-if="hasManyOptions"
                    @mousemove="selectAllMousemoveHandler"
                    @mousedown="selectAllMousedownHandler"
                    @click="selectAllClickHandler">
                    <div class="cfd-option-fake">
                        <svg class="cfd-checkbox is-unchecked" fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                            <path d="M0 0h24v24H0z" fill="none"/>
                        </svg>
                        <svg class="cfd-checkbox is-interminate" fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                            <defs>
                                <path d="M0 0h24v24H0z" id="a"/>
                            </defs>
                            <clipPath id="b">
                                <use overflow="visible" xlink:href="#a"/>
                            </clipPath>
                            <path clip-path="url(#b)" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10H7v-2h10v2z"/>
                        </svg>
                        <svg class="cfd-checkbox is-checked" fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 0h24v24H0z" fill="none"/>
                            <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    </div>
                    <div class="cfd-option-text">{{ labelAll }}</div>
                </div>
                <div class="cfd-check-divider" v-if="hasManyOptions"></div>
                <div class="cfd-dropdown-scroller" ref="scroller">
                    <div :class="optionClass({ option, index })" v-for="(option, index) in options" :key="option.value"
                        @mousemove="optionMousemoveHandler(option, index)"
                        @mousedown="optionMousedownHandler(option, index)"
                        @click="optionClickHandler($event, option)">
                        <div class="cfd-option-fake">
                            <svg class="cfd-checkbox is-unchecked" fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                                <path d="M0 0h24v24H0z" fill="none"/>
                            </svg>
                            <svg class="cfd-checkbox is-checked" fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 0h24v24H0z" fill="none"/>
                                <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                        </div>
                        <div class="cfd-option-text">{{ option.text }}</div>
                        <div class="cfd-option-help" v-if="option.help" :data-tipok-title="option.help">
                            <svg class="cfd-help" fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 0h24v24H0z" fill="none"/>
                                <path d="M11 17h2v-6h-2v6zm1-15C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zM11 9h2V7h-2v2z"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
    <input type="hidden" :name="name" v-model="localValue" :disabled="disabled">
</div>
</template>

<script>
import i18n from '../../i18n';

export default {
    name: 'CheckboxSelector',

    props: {
        label: {
            type: String,
            required: true
        },

        value: Array,

        name: {
            type: String,
            default () {
                return `uid-${this._uid}`;
            }
        },

        tabindex: {
            type: [String, Number],
            default: 0
        },

        options: {
            type: Array,
            required: true
        },

        disabled: {
            type: Boolean,
            default: false
        }
    },

    data () {
        return {
            isFocused: false,
            isOpened: false,
            isFloated: false,
            highlightedOptionIndex: -1,
            isSelectAllOptionHighlighted: false,
            labelAll: i18n('All'),
            isLeaveCancelled: false,
            isTouchMoved: false,
            isTouchHandled: false
        };
    },

    computed: {
        localValue: {
            get () {
                return this.value;
            },
            set (value) {
                this.$emit('input', value);
            }
        },

        areAllSelected () {
            return this.selectedOptionsCount === this.options.length;
        },

        selectorClass () {
            return {
                'cfd-selector': true,
                'is-focused': this.isFocused,
                'is-opened': this.isOpened,
                'is-floated': this.isFloated,
                'is-filled': !!this.selectedOptionsCount,
                'is-disabled': this.disabled
            };
        },

        optionClass () {
            return ({ option, index }) => ({
                'cfd-option': true,
                'is-selected': this.selectedOptions.includes(option),
                'is-highlighted': index === this.highlightedOptionIndex,
                'is-disabled': option.disabled,
                'has-help': option.help
            });
        },

        highlightedOption () {
            return this.options[this.highlightedOptionIndex];
        },

        selectedOptions () {
            return this.options.filter(option => this.localValue.includes(option.value));
        },

        selectedOptionsIndexes () {
            return this.selectedOptions.map(option => this.options.indexOf(option));
        },

        selectedOptionsText () {
            return this.selectedOptions.map(option => option.text).join(', ');
        },

        selectedOptionsCount () {
            return this.selectedOptions.length;
        },

        optionsCount () {
            return this.options.length;
        },

        hasManyOptions () {
            return this.optionsCount > 5;
        },

        isInterminate () {
            return this.selectedOptionsCount > 0 && this.selectedOptionsCount < this.optionsCount;
        }
    },

    methods: {
        labelMousedownHandler () {
            if (this.isFocused) {
                this.isFloated = true;
            }
        },

        labelClickHandler () {
            this.$refs.input.focus();
        },

        inputFocusHandler () {
            if (this.disabled) {
                return;
            }
            this.isFocused = true;
            this.isFloated = false;
        },

        inputBlurHandler () {
            this.isFocused = false;
        },

        inputClickHandler (ev) {
            if (this.disabled) {
                return;
            }
            // helps IE to receive focus
            this.$refs.input.focus();
            const isInnerClicked = !!ev.target.closest('.cfd-inner');
            if (isInnerClicked) {
                this.isOpened = !this.isOpened;
            }
        },

        inputKeyUpHandler () {
            if (this.disabled) {
                return;
            }
            if (this.isOpened) {
                if (this.highlightedOptionIndex === -1) {
                    return;
                }
                if (this.highlightedOptionIndex === 0) {
                    if (this.hasManyOptions) {
                        this.highlightedOptionIndex = -1;
                        this.isSelectAllOptionHighlighted = true;
                    }
                    return;
                }
                this.highlightedOptionIndex--;
                this.scrollUpToHighlighted();
            }
        },

        inputKeyDownHandler () {
            if (this.disabled) {
                return;
            }
            if (this.isOpened) {
                if (this.highlightedOptionIndex === this.options.length - 1) {
                    return;
                }
                this.isSelectAllOptionHighlighted = false;
                this.highlightedOptionIndex++;
                this.scrollDownToHighlighted();
            }
        },

        inputEnterHandler () {
            if (this.disabled) {
                return;
            }
            if (this.isOpened) {
                if (this.highlightedOptionIndex !== -1) {
                    if (this.highlightedOption.disabled) {
                        return;
                    }
                    if (this.localValue.includes(this.highlightedOption.value)) {
                        this.removeValue(this.highlightedOption.value);
                    } else {
                        this.addValue(this.highlightedOption.value);
                    }
                } else if (this.isSelectAllOptionHighlighted) {
                    if (this.areAllSelected) {
                        this.removeAll();
                    } else {
                        if (this.areAllEnabledSelected()) {
                            this.removeAll();
                        } else {
                            this.addAll();
                        }
                    }
                }
            } else {
                this.isOpened = true;
            }
        },

        inputEscHandler () {
            this.isOpened = false;
        },

        inputTabHandler () {
            this.isOpened = false;
        },

        selectAllMousemoveHandler () {
            this.isSelectAllOptionHighlighted = true;
            this.highlightedOptionIndex = -1;
        },

        selectAllMousedownHandler () {
            this.isSelectAllOptionHighlighted = true;
            this.highlightedOptionIndex = -1;
        },

        selectAllClickHandler () {
            if (this.areAllSelected) {
                this.removeAll();
            } else {
                if (this.areAllEnabledSelected()) {
                    this.removeAll();
                } else {
                    this.addAll();
                }
            }
        },

        optionMousemoveHandler (option, index) {
            this.isSelectAllOptionHighlighted = false;
            this.highlightedOptionIndex = index;
        },

        optionMousedownHandler (option, index) {
            if (option.disabled) {
                return;
            }
            this.highlightedOptionIndex = index;
        },

        optionClickHandler (ev, option) {
            const isHelpClicked = !!ev.target.closest('.cfd-option-help');
            if (isHelpClicked || option.disabled) {
                return;
            }
            if (this.localValue.includes(option.value)) {
                this.removeValue(option.value);
            } else {
                this.addValue(option.value);
            }
        },

        addValue (value) {
            this.localValue = this.options
                .filter(option => this.localValue.includes(option.value) || option.value === value)
                .map(option => option.value);
        },

        removeValue (value) {
            this.localValue = this.options
                .filter(option => this.localValue.includes(option.value) && option.value !== value)
                .map(option => option.value);
        },

        addAll () {
            this.localValue = this.options
                .filter(option => this.localValue.includes(option.value) || !option.disabled)
                .map(option => option.value);
        },

        removeAll () {
            this.localValue = this.options
                .filter(option => this.localValue.includes(option.value) && option.disabled)
                .map(option => option.value);
        },

        scrollUpToHighlighted () {
            const highlightedOptionElement = this.$refs.scroller.children[this.highlightedOptionIndex];
            const delta = this.$refs.scroller.scrollTop - highlightedOptionElement.offsetTop;
            if (delta > 0) {
                this.$refs.scroller.scrollTop = highlightedOptionElement.offsetTop;
            }
        },

        scrollDownToHighlighted () {
            const highlightedOptionElement = this.$refs.scroller.children[this.highlightedOptionIndex];
            const highlightedOptionElementHeight = highlightedOptionElement.clientHeight;
            const delta = (highlightedOptionElement.offsetTop + highlightedOptionElementHeight) - (this.$refs.scroller.scrollTop + this.$refs.scroller.clientHeight);
            if (delta > 0) {
                this.$refs.scroller.scrollTop = this.$refs.scroller.scrollTop + delta;
            }
        },

        getNextActiveOptionIndex ({ startIndex }) {
            const nextActiveOption = this.options
                .slice(startIndex + 1)
                .find(option => !option.disabled);
            return this.options.indexOf(nextActiveOption);
        },

        areAllEnabledSelected () {
            return this.options
                .filter(option => !option.disabled)
                .every(option => this.selectedOptions.includes(option));
        },

        beforeEnter () {
            if (this.isLeaveCancelled) {
                return;
            }
            if (this.selectedOptions.length) {
                this.highlightedOptionIndex = this.selectedOptionsIndexes[0];
            } else {
                const nextActiveOptionIndex = this.getNextActiveOptionIndex({
                    startIndex: 0
                });
                if (nextActiveOptionIndex !== -1) {
                    this.highlightedOptionIndex = nextActiveOptionIndex;
                }
            }
        },

        enter () {
            if (this.isLeaveCancelled) {
                return;
            }
            this.$refs.scroller.scrollTop = 0;
            this.scrollDownToHighlighted();
        },

        afterLeave () {
            this.highlightedOptionIndex = -1;
            this.isSelectAllOptionHighlighted = false;
            this.isLeaveCancelled = false;
        },

        leaveCancelled () {
            this.isLeaveCancelled = true;
        },

        globalTouchstartHandler () {
            this.isTouchMoved = false;
            this.isTouchHandled = false;
        },

        globalTouchmoveHandler () {
            this.isTouchMoved = true;
        },

        globalTouchendHandler (ev) {
            this.isTouchHandled = true;
            if (this.isTouchMoved) {
                return;
            }
            const closestInput = ev.target.closest && ev.target.closest('.cfd-input');
            const closestLabel = ev.target.closest && ev.target.closest('.cfd-label');
            if (closestInput === this.$refs.input || closestLabel === this.$refs.label) {
                return;
            }
            this.$refs.input.blur();
            this.isOpened = false;
        },

        globalMousedownHandler (ev) {
            if (this.isTouchHandled) {
                return;
            }
            const closestInput = ev.target.closest && ev.target.closest('.cfd-input');
            const closestLabel = ev.target.closest && ev.target.closest('.cfd-label');
            if (closestInput === this.$refs.input || closestLabel === this.$refs.label) {
                return;
            }
            this.$refs.input.blur();
            this.isOpened = false;
        }
    },

    mounted () {
        document.addEventListener('touchstart', this.globalTouchstartHandler);
        document.addEventListener('touchmove', this.globalTouchmoveHandler);
        document.addEventListener('touchend', this.globalTouchendHandler);
        document.addEventListener('mousedown', this.globalMousedownHandler);
    },

    destroyed () {
        document.removeEventListener('touchstart', this.globalTouchstartHandler);
        document.removeEventListener('touchmove', this.globalTouchmoveHandler);
        document.removeEventListener('touchend', this.globalTouchendHandler);
        document.removeEventListener('mousedown', this.globalMousedownHandler);
    }
};
</script>

<style scoped>
.cfd-selector {
    position: relative;
    z-index: 1;
    margin-top: 28px;
}

.cfd-input {
    position: relative;
    z-index: 1;
    outline: 0;
}

.cfd-inner {
    border-bottom: 1px solid rgba(0, 0, 0, .42);
    padding: 6px 24px 6px 0;
    cursor: default;
    box-shadow: inset 0 -2px 0 -1px transparent;
    transition: border .125s ease-in, box-shadow .125s ease-in;
}

.cfd-inner:hover {
    border-bottom-color: rgba(0, 0, 0, .87);
    transition-timing-function: ease-out;
    box-shadow: inset 0 -2px 0 -1px rgba(0, 0, 0, .87);
}

.cfd-selector.is-focused .cfd-inner,
.cfd-selector.is-opened .cfd-inner {
    border-bottom-color: #f60;
    box-shadow: inset 0 -2px 0 -1px #f60;
}

.cfd-selector.is-disabled .cfd-inner {
    border-bottom: 1px dotted rgba(0, 0, 0, .42);
    box-shadow: none;
}

.cfd-label {
    position: absolute;
    z-index: 1;
    top: 6px;
    font-size: 16px;
    line-height: 20px;
    color: rgba(0, 0, 0, .54);
    cursor: default;
    transition: all .125s ease-in;
}

.cfd-selector.is-filled .cfd-label,
.cfd-selector.is-focused .cfd-label,
.cfd-selector.is-opened .cfd-label,
.cfd-selector.is-floated .cfd-label {
    top: -14px;
    font-size: 12px;
    line-height: 14px;
    transition-timing-function: ease-out;
}

.cfd-selector.is-focused .cfd-label,
.cfd-selector.is-opened .cfd-label,
.cfd-selector.is-floated .cfd-label {
    color: rgba(255, 102, 0, .87);
}

.cfd-selector.is-disabled .cfd-label {
    color: rgba(0, 0, 0, .38);
    transition-timing-function: ease-out;
}

.cfd-selected {
    font-size: 16px;
    line-height: 20px;
    height: 20px;
    overflow: hidden;
    color: rgba(0, 0, 0, .87);
    white-space: nowrap;
    text-overflow: ellipsis;
    pointer-events: none;
}

.cfd-selector.is-disabled .cfd-selected {
    color: rgba(0, 0, 0, .38);
}

.cfd-icon {
    position: absolute;
    z-index: 1;
    top: 4px;
    right: 0;
    width: 24px;
    height: 24px;
    opacity: .54;
    transition: opacity .125s ease-in, transform .25s ease-in;
}

.cfd-selector.is-disabled .cfd-icon {
    opacity: .38;
    transition-timing-function: ease-out;
}

.cfd-selector.is-opened .cfd-icon {
    transform: rotateZ(180deg);
    transition-timing-function: ease-out;
}

.cfd-dropdown {
    position: absolute;
    z-index: 1;
    top: 100%;
    right: 0;
    left: 0;
    padding-top: 8px;
    padding-bottom: 8px;
    background-color: #fff;
    box-shadow: 0 8px 10px 1px rgba(0, 0, 0, .14),
        0 3px 14px 3px rgba(0, 0, 0, .12);
}

.cfd-dropdown-scroller {
    position: relative;
    z-index: 1;
    max-height: 240px;
    overflow-y: auto;
}

.cfd-check-divider {
    margin-top: 8px;
    margin-bottom: 8px;
    border-top: 0;
    border-bottom: 1px solid rgba(0, 0, 0, .12);
}

.cfd-option {
    position: relative;
    display: flex;
    cursor: default;
    transition: background-color .125s ease-in;
}

.cfd-option.is-highlighted {
    background-color: rgba(0, 0, 0, .06);
    transition-timing-function: ease-out;
}

.cfd-option.is-highlighted.is-disabled {
    background-color: rgba(0, 0, 0, .03);
    transition-timing-function: ease-out;
}

.cfd-option.has-help .cfd-option-text {
    padding-right: 56px;
}

.cfd-option-fake {
    position: relative;
    flex-shrink: 0;
    margin-right: 4px;
    margin-left: 4px;
    border-radius: 50%;
    padding: 12px;
    width: 24px;
    height: 24px;
    opacity: .54;
    transition: opacity .125s ease-in;
}

.cfd-option.is-highlighted .cfd-option-fake,
.cfd-option.is-selected .cfd-option-fake,
.cfd-option.is-interminate .cfd-option-fake {
    opacity: .87;
    transition-timing-function: ease-out;
}

.cfd-option.is-disabled .cfd-option-fake {
    opacity: .38;
    transition-timing-function: ease-out;
}

.cfd-checkbox {
    max-width: 100%;
    height: auto;
}

.cfd-option.is-selected .cfd-checkbox.is-unchecked,
.cfd-option.is-interminate .cfd-checkbox.is-unchecked {
    display: none;
}

.cfd-checkbox.is-interminate {
    display: none;
}

.cfd-option.is-interminate .cfd-checkbox.is-interminate {
    display: block;
    fill: #f60;
}

.cfd-checkbox.is-checked {
    fill: #f60;
    display: none;
}

.cfd-option.is-selected .cfd-checkbox.is-checked {
    display: block;
}

.cfd-option-text {
    flex-grow: 1;
    padding: 14px 16px;
    font-size: 16px;
    line-height: 20px;
}

.cfd-option.is-disabled .cfd-option-text {
    color: rgba(0, 0, 0, .38);
}

.cfd-option-help {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 4px;
    padding: 12px;
    width: 24px;
    height: 24px;
    opacity: .54;
    transition: opacity .125s ease-in;
}

.cfd-option-help:hover {
    opacity: .87;
    transition-timing-function: ease-out;
}

.cfd-help {
    max-width: 100%;
    height: auto;
}

@media (min-width: 720px) {
    .cfd-dropdown-scroller {
        max-height: 280px;
    }

    .cfd-option.has-help .cfd-option-text {
        padding-right: 52px;
    }

    .cfd-option-fake {
        margin-right: -2px;
        margin-left: 6px;
        padding: 10px;
        width: 20px;
        height: 20px;
    }

    .cfd-option-text {
        padding-top: 10px;
        padding-bottom: 10px;
    }

    .cfd-option-help {
        top: 4px;
        right: 10px;
        padding: 6px;
        width: 20px;
        height: 20px;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition-property: opacity;
    transition-duration: .25s;
}

.fade-enter-active {
    transition-timing-function: ease-out;
}

.fade-leave-active {
    transition-timing-function: ease-in;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-to,
.fade-leave {
    opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/selectors/CheckboxSelector.vue